<template>
  <b-modal v-model="visible" size="lg" centered hide-header hide-footer
           no-close-on-backdrop body-class="p-0" content-class="fam-content">
    <!-- Hero -->
    <div class="fam__hero">
      <div class="fam__hero-icon"><i class="fa-solid fa-sliders"></i></div>
      <div class="fam__hero-text">
        <h4 class="mb-0">
          {{ isBulk ? $t('finance.actionModal.bulkTitle', { count: targets.length }) : $t('finance.actionModal.title') }}
        </h4>
        <small v-if="!isBulk && target">
          #{{ target.id }} · {{ target.clientDetails && target.clientDetails.email }} ·
          <strong>{{ convertToEURO(target.amount) }}</strong>
        </small>
        <small v-else>{{ targets.length }} {{ $t('finance.actionModal.selected') }}</small>
      </div>
      <button type="button" class="fam__close" @click="close" :disabled="running">
        <feather-icon icon="XIcon" size="18"/>
      </button>
    </div>

    <div class="fam__body">
      <!-- Action options -->
      <div class="fam__label">{{ $t('finance.actionModal.pickAction') }}</div>
      <div class="fam__options">
        <button v-for="opt in options" :key="opt.key" type="button" class="fam-opt"
                :class="[{ 'is-active': action === opt.key }, `fam-opt--${opt.tone}`]"
                :disabled="running" @click="action = opt.key">
          <span class="fam-opt__icon"><i :class="opt.icon"></i></span>
          <span class="fam-opt__body">
            <span class="fam-opt__title">{{ opt.title }}</span>
            <span class="fam-opt__hint">{{ opt.hint }}</span>
          </span>
        </button>
      </div>

      <!-- Existing finance comment (read-only, so the previous note is visible before writing a new one) -->
      <div v-if="target && target.financeComment" class="mt-1">
        <div class="fam__label">{{ $t('finance.actionModal.previousComment') }}</div>
        <blockquote class="fam-prev">
          <i class="fa-solid fa-quote-left fam-prev__quote"></i>
          <div class="fam-prev__body">
            <div class="fam-prev__text">{{ target.financeComment }}</div>
            <small v-if="target.financeUserName || target.financeTime" class="fam-prev__meta">
              <template v-if="target.financeUserName">{{ target.financeUserName }}</template><template v-if="target.financeTime"> · {{ target.financeTime | momentDateTime }}</template>
            </small>
          </div>
        </blockquote>
      </div>

      <!-- Comment -->
      <div class="fam__label mt-1">{{ $t('finance.comment') }}</div>
      <b-form-textarea v-model="comment" rows="3" :disabled="running"
                       :placeholder="$t('finance.commentPlaceholder')"/>
      <small v-if="action === 'comment' && !comment.trim()" class="text-danger">
        {{ $t('finance.actionModal.commentRequired') }}
      </small>

      <!-- Progress -->
      <div v-if="running || progress.total > 0" class="fam__progress mt-1">
        <b-progress :value="progress.done" :max="progress.total" height="8px"
                    :variant="progress.fail ? 'danger' : 'success'"/>
        <small class="text-muted">
          <template v-if="running">{{ $t('finance.actionModal.running', { done: progress.done, total: progress.total }) }}</template>
          <template v-else>
            {{ $t('finance.actionModal.resultOk', { count: progress.ok }) }}<template v-if="progress.fail"> · <span class="text-danger">{{ $t('finance.actionModal.resultFail', { count: progress.fail }) }}</span></template>
          </template>
        </small>
      </div>

      <!-- Footer -->
      <div class="fam__footer">
        <b-button variant="outline-secondary" :disabled="running" @click="close">
          {{ $t('operations.cancel') }}
        </b-button>
        <b-button :variant="executeVariant" :disabled="running || !canExecute" @click="run">
          <b-spinner v-if="running" small class="mr-25"/>
          <i v-else :class="activeOption.icon" class="mr-25"></i>
          {{ $t('finance.actionModal.execute') }}
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex'
import mixinBase from '@/components/mixin/mixinBase'
import mixinNotifications from '@/components/mixin/mixinNotifications'

export default {
  name: 'FinanceActionModal',
  mixins: [mixinBase, mixinNotifications],
  data: () => ({
    visible: false,
    targets: [],
    action: 'approve',
    comment: '',
    running: false,
    progress: { done: 0, total: 0, ok: 0, fail: 0 },
  }),
  computed: {
    isBulk() {
      return this.targets.length > 1
    },
    target() {
      return this.targets.length === 1 ? this.targets[0] : null
    },
    options() {
      return [
        { key: 'comment', tone: 'info', icon: 'fa-solid fa-comment-dots', title: this.$t('finance.comment'), hint: this.$t('finance.actionModal.commentHint') },
        { key: 'approve', tone: 'success', icon: 'fa-solid fa-circle-check', title: this.$t('finance.approve'), hint: this.$t('finance.actionModal.confirmHint') },
        { key: 'cancel', tone: 'danger', icon: 'fa-solid fa-rotate-left', title: this.$t('finance.actionModal.cancelTitle'), hint: this.$t('finance.actionModal.cancelHint') },
        { key: 'fake', tone: 'warning', icon: 'fa-solid fa-wand-magic-sparkles', title: this.$t('finance.actionModal.fakeTitle'), hint: this.$t('finance.actionModal.fakeHint') },
      ]
    },
    activeOption() {
      return this.options.find(o => o.key === this.action) || this.options[0]
    },
    executeVariant() {
      const map = { info: 'info', success: 'success', danger: 'danger', warning: 'warning' }
      return map[this.activeOption.tone] || 'primary'
    },
    canExecute() {
      if (!this.targets.length) return false
      if (this.action === 'comment') return this.comment.trim().length > 0
      return true
    },
  },
  methods: {
    ...mapActions('monetary', ['financeEvent', 'updateMonetaryRealStatus']),
    /** @param {Array<Object>} items transaction row objects (1 = single mode) */
    open(items) {
      this.targets = Array.isArray(items) ? items.filter(Boolean) : [items].filter(Boolean)
      this.action = 'approve'
      this.comment = ''
      this.running = false
      this.progress = { done: 0, total: 0, ok: 0, fail: 0 }
      this.visible = true
    },
    close() {
      if (this.running) return
      this.visible = false
    },
    async run() {
      if (!this.canExecute || this.running) return
      const $this = this
      this.running = true
      this.progress = { done: 0, total: this.targets.length, ok: 0, fail: 0 }
      const updated = []
      for (const tx of this.targets) {
        try {
          let result
          if (this.action === 'fake') {
            result = await this.updateMonetaryRealStatus({ id: tx.id, realType: 'FAKE' })
          } else {
            result = await this.financeEvent({ id: tx.id, event: this.action, comment: this.comment })
          }
          if (result) updated.push(result)
          this.progress.ok++
        } catch (err) {
          this.progress.fail++
        }
        this.progress.done++
      }
      this.running = false
      if (this.progress.fail === 0) {
        this.showSuccessNotification(this.$t('finance.actionModal.resultOk', { count: this.progress.ok }))
        this.visible = false
      } else {
        this.showWarningNotification(
            `${this.$t('finance.actionModal.resultOk', { count: this.progress.ok })} · ${this.$t('finance.actionModal.resultFail', { count: this.progress.fail })}`)
      }
      $this.$emit('done', updated)
    },
  },
}
</script>

<style lang="scss">
.fam-content {
  border: none;
  border-radius: .8rem;
  overflow: hidden;
}
.fam__hero {
  display: flex;
  align-items: center;
  gap: .9rem;
  padding: 1.1rem 1.4rem;
  color: #fff;
  background: linear-gradient(120deg, #1f2a44, #3b5b92);
}
.fam__hero-icon {
  width: 44px;
  height: 44px;
  border-radius: .65rem;
  background: rgba(255, 255, 255, .18);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;
}
.fam__hero-text { flex: 1; min-width: 0; h4 { color: #fff; font-weight: 700; } small { opacity: .9; } }
.fam__close {
  border: none;
  background: rgba(255, 255, 255, .15);
  color: #fff;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background .15s ease, transform .2s ease;
  &:hover { background: rgba(255, 255, 255, .3); transform: rotate(90deg); }
}

.fam__body { padding: 1.15rem 1.4rem 1.3rem; }
.fam__label {
  font-size: .74rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .4px;
  color: #a6a4b0;
  margin-bottom: .5rem;
}

.fam__options {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: .55rem;
}
.fam-opt {
  --fam-c: #7367f0;
  display: flex;
  align-items: center;
  gap: .6rem;
  border: 1px solid #d8d6de;
  border-radius: .65rem;
  background: transparent;
  padding: .6rem .7rem;
  cursor: pointer;
  text-align: left;
  transition: all .15s ease;

  &--info { --fam-c: #00cfe8; .fam-opt__icon { background: rgba(0, 207, 232, .14); } }
  &--success { --fam-c: #28c76f; .fam-opt__icon { background: rgba(40, 199, 111, .14); } }
  &--danger { --fam-c: #ea5455; .fam-opt__icon { background: rgba(234, 84, 85, .14); } }
  &--warning { --fam-c: #ff9f43; .fam-opt__icon { background: rgba(255, 159, 67, .14); } }

  &:hover:not(:disabled) { border-color: var(--fam-c); }
  &.is-active {
    border-color: var(--fam-c);
    box-shadow: inset 0 0 0 1px var(--fam-c);
    background: rgba(0, 0, 0, .015);
  }
  &__icon {
    width: 34px;
    height: 34px;
    border-radius: .55rem;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--fam-c);
    font-size: .9rem;
  }
  &__body { min-width: 0; display: flex; flex-direction: column; line-height: 1.25; }
  &__title { font-size: .84rem; font-weight: 700; color: #5e5873; }
  &__hint { font-size: .7rem; color: #b9b9c3; }
}

.fam-prev {
  display: flex;
  align-items: flex-start;
  gap: .6rem;
  margin: 0;
  padding: .6rem .8rem;
  border-left: 3px solid #00cfe8;
  border-radius: .45rem;
  background: rgba(0, 207, 232, .07);

  &__quote { color: #00cfe8; font-size: .8rem; margin-top: .15rem; flex-shrink: 0; }
  &__body { min-width: 0; display: flex; flex-direction: column; gap: .15rem; }
  &__text { font-size: .84rem; color: #5e5873; white-space: pre-wrap; word-break: break-word; }
  &__meta { color: #b9b9c3; }
}

.fam__progress { display: flex; flex-direction: column; gap: .25rem; }
.fam__footer {
  display: flex;
  justify-content: flex-end;
  gap: .5rem;
  margin-top: 1.1rem;
}

.dark-layout {
  .fam-content { background: #283046; color: #d0d2d6; }
  .fam-opt {
    border-color: #3b4253;
    &.is-active { background: rgba(255, 255, 255, .03); }
    &__title { color: #d0d2d6; }
  }
  .fam-opt__title { color: #d0d2d6; }
  .fam-prev {
    background: rgba(0, 207, 232, .1);
    &__text { color: #d0d2d6; }
  }
}
</style>
